import isInjuredStatus from './isInjuredStatus';

const QB_POSITION = 'quarterback';
const TETHERED_POSITIONS = ['wide receiver', 'tight end', 'running back'];
/** WR1/WR2, TE1/TE2, and RB1/RB2 stay coupled to the starter's health. */
const PRIMARY_SKILL_DEPTH_MAX = 2;

const POSITION_ABBREV = {
	'wide receiver': 'WR',
	'tight end': 'TE',
	'running back': 'RB'
};

function normalizePlayer(player) {
	if (!player) return null;
	return Array.isArray(player) ? player[0] : player;
}

function playerTeamId(player) {
	return player?.team_id ?? player?.team?.id ?? null;
}

function quarterbacksForTeam(player, teamQuarterbacksByTeamId) {
	if (teamQuarterbacksByTeamId) {
		const teamId = playerTeamId(player);
		if (!teamId) return [];

		return (
			teamQuarterbacksByTeamId[teamId] ||
			teamQuarterbacksByTeamId[String(teamId)] ||
			[]
		);
	}

	return (player?.player_same_team_grade_rank_data?.talent_grades || []).filter(
		(peer) => peer.position === QB_POSITION
	);
}

function isPrimarySkillPlayer(player) {
	const depth = player.depth_chart_position;
	if (depth == null) return false;
	return depth <= PRIMARY_SKILL_DEPTH_MAX;
}

function startingQuarterback(quarterbacks) {
	if (!quarterbacks.length) return null;

	const qb1 = quarterbacks.find((qb) => qb.depth_chart_position === 1);
	return qb1 || quarterbacks[0];
}

function skillPositionAbbrev(player) {
	return POSITION_ABBREV[player.position] || '';
}

function volumeLabel(position) {
	return position === 'running back' ? 'workload' : 'target share';
}

/**
 * Heuristic: top-of-chart WR/TE/RB whose fantasy ceiling is tethered to a hurt QB1.
 * Returns alert metadata for UI, or null when the signal does not apply.
 */
export function getWrTetheredInjuredQbAlert(
	playerInput,
	teamQuarterbacksByTeamId = null
) {
	const player = normalizePlayer(playerInput);
	if (
		!player ||
		!TETHERED_POSITIONS.includes(player.position) ||
		!playerTeamId(player)
	) {
		return null;
	}

	if (!isPrimarySkillPlayer(player)) {
		return null;
	}

	const quarterbacks = quarterbacksForTeam(
		player,
		teamQuarterbacksByTeamId
	).sort(
		(a, b) => (a.depth_chart_position ?? 99) - (b.depth_chart_position ?? 99)
	);

	const starter = startingQuarterback(quarterbacks);
	if (!starter || !isInjuredStatus(starter.status)) {
		return null;
	}

	const qbName = `${starter.first_name} ${starter.last_name}`;
	const injurySuffix = starter.injury_description
		? ` (${starter.injury_description})`
		: '';
	const abbrev = skillPositionAbbrev(player);

	return {
		qbId: starter.id,
		qbFirstName: starter.first_name,
		qbLastName: starter.last_name,
		qbStatus: starter.status,
		qbInjuryDescription: starter.injury_description,
		message: `${qbName} — ${starter.status}${injurySuffix}`,
		tooltipTitle: 'Tethered to injured QB',
		tooltipBody: `${abbrev}${player.depth_chart_position} ${volumeLabel(player.position)} is tied to ${qbName}. Starter is ${starter.status}; backup play-calling and volume may shift weekly upside.`
	};
}

export default getWrTetheredInjuredQbAlert;
